import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
};
const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  data: null,
};

const initialState = {
  addTrainingData: { ...mutationInitialState },
  deleteTrainingData: { ...mutationInitialState },
  trainingData: { ...fetchDataInitialState },
  updateTrainingData: { ...mutationInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const addExpertBotTrainingData = thunk(
  'expertBots/addTrainingData',
  async (token, { botId, payload }) => {
    const r = await axios.put(`/cs/expert-bots/${botId}/train`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to add training data');
  },
);

export const deleteTrainingDataThunk = thunk(
  'expertBots/deleteTrainingData',
  async (token, docId) => {
    const r = await axios.delete(`/cs/expert-bots/train/doc/${docId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete training data');
  },
);

export const fetchTrainingData = thunk(
  'expertBots/fetchTrainingData',
  async (token, botId) => {
    const r = await axios.get('/cs/expert-bots/training-data', {
      params: { 'request-id': uuidv4(), botId },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch training data');
  },
);

export const updateExpertBotTrainingDataThunk = thunk(
  'expertBots/updateTrainingData',
  async (token, { docId, payload }) => {
    const r = await axios.put(`/cs/expert-bots/train/doc/${docId}`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to update training data');
  },
);

const addM = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].processing = true;
      s[k].done = false;
      s[k].failed = false;
    })
    .addCase(t.fulfilled, (s) => {
      s[k].processing = false;
      s[k].done = true;
      s[k].failed = false;
    })
    .addCase(t.rejected, (s, a) => {
      s[k].processing = false;
      s[k].done = false;
      s[k].failed = true;
      s[k].error = a.payload;
    });

const addF = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].fetching = true;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = false;
    })
    .addCase(t.fulfilled, (s, a) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = true;
      s[k].fetchingFailed = false;
      s[k].data = a.payload;
    })
    .addCase(t.rejected, (s) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = true;
    });

const expertBotsSlice = createSlice({
  name: 'expertBots',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addM(builder, addExpertBotTrainingData, 'addTrainingData');
    addM(builder, deleteTrainingDataThunk, 'deleteTrainingData');
    addF(builder, fetchTrainingData, 'trainingData');
    addM(builder, updateExpertBotTrainingDataThunk, 'updateTrainingData');
  },
});

export default expertBotsSlice.reducer;
